import Axios from 'axios';
import { useQuery, UseQueryOptions } from 'react-query';

import { basePath } from '@bai/utils';

import { ApiError, isApiError } from './api-error';

export function useData<
  T,
  Options extends UseQueryOptions<T, Error> = UseQueryOptions<T, Error>,
>(
  endpoint: string,
  options?: Options, // pass in { enabled: false } if the endpoint should not be called
  responseOn404?: T,
  fetchOptions?: Parameters<typeof fetch>[1],
  cacheKey?: string,
  isPublic?: boolean,
) {
  return useQuery<T, Error>(
    cacheKey ?? endpoint,
    () =>
      fetch(
        `${basePath}api/${isPublic ? 'public/' : ''}${endpoint}`,
        fetchOptions,
      )
        .then((res) => {
          if (res.status === 404 && responseOn404 !== undefined)
            return responseOn404;
          if (!res.ok) {
            if (res.status === 307) {
              throw Error('Temporary Redirect');
            }
            if (res.status === 503) {
              throw new Error('Service Unavailable');
            }
            throw new Error(res.statusText);
          }
          return res.json();
        })
        .then((data: T | ApiError) => {
          if (isApiError(data)) {
            throw new Error(data.ErrorMessage);
          }
          return data;
        }),
    { refetchOnWindowFocus: false, retry: 0, ...options },
  );
}

export const axios = Axios.create();

export default useData;
